// Akai MPC2000 / MPC2000XL / MPC3000 Floppy Disk Image Exporter
//
// Creates a standard 1.44 MB FAT12 floppy disk image (.img) containing
// Akai MPC SND files with 16-bit signed LE PCM audio.
//
// The MPC2000 family uses standard DOS-formatted floppies (FAT12), so
// no custom disk geometry is needed — just a valid FAT12 filesystem with
// .SND files in the root directory.
//
// SND file format (42-byte header + raw PCM):
//   0x00  2   File type (0x01, 0x04)
//   0x02  16  Filename (ASCII, space-padded, no extension)
//   0x12  1   Null (0x00)
//   0x13  1   Level (0-200, default 100)
//   0x14  1   Tune (-120 to +120, signed)
//   0x15  1   Channels (0=Mono, 1=Stereo)
//   0x16  4   Start point (u32 LE)
//   0x1A  4   Loop end point (u32 LE)
//   0x1E  4   End point (u32 LE)
//   0x22  4   Loop length (u32 LE)
//   0x26  1   Loop mode (0=Off, 1=On)
//   0x27  1   Beats in loop (1-16, default 1)
//   0x28  2   Sample rate (u16 LE, default 44100)
//   0x2A  ... 16-bit signed LE PCM data
//
// Works with Gotek floppy emulators and real MPC2000/MPC2000XL/MPC3000.
//
// Format reference: pyMPC2000xl (github.com/maseology/pyMPC2000xl),
// libsndfile SF_FORMAT_MPC2K.

// ---- FAT12 1.44MB floppy geometry -----------------------------------------
export const MPC_DISK_SIZE    = 1474560;   // 1.44 MB (2880 × 512)
export const MPC_SECTOR_SIZE  = 512;
export const MPC_TOTAL_SECTORS = 2880;
export const MPC_SECTORS_PER_CLUSTER = 1;
export const MPC_RESERVED_SECTORS = 1;
export const MPC_NUM_FATS = 2;
export const MPC_SECTORS_PER_FAT = 9;
export const MPC_ROOT_ENTRIES = 224;
export const MPC_ROOT_DIR_SECTORS = 14;    // 224 × 32 / 512
export const MPC_MEDIA_DESCRIPTOR = 0xF0;  // 3.5" HD floppy

// Cluster 2 starts at this sector (after boot + 2 FATs + root dir)
export const MPC_DATA_START_SECTOR = MPC_RESERVED_SECTORS +
   MPC_NUM_FATS * MPC_SECTORS_PER_FAT + MPC_ROOT_DIR_SECTORS; // 33
export const MPC_DATA_START_BYTE = MPC_DATA_START_SECTOR * MPC_SECTOR_SIZE; // 0x4200
export const MPC_DATA_BYTES = MPC_DISK_SIZE - MPC_DATA_START_BYTE; // ~1.45MB
export const MPC_MAX_CLUSTERS = Math.floor(MPC_DATA_BYTES / MPC_SECTOR_SIZE); // 2847

// Root directory starts after boot + 2 FATs
export const MPC_ROOT_DIR_OFFSET = (MPC_RESERVED_SECTORS + MPC_NUM_FATS * MPC_SECTORS_PER_FAT) * MPC_SECTOR_SIZE;
export const MPC_ROOT_DIR_BYTES = MPC_ROOT_ENTRIES * 32;

// FAT starts after boot sector
export const MPC_FAT1_OFFSET = MPC_RESERVED_SECTORS * MPC_SECTOR_SIZE;
export const MPC_FAT2_OFFSET = (MPC_RESERVED_SECTORS + MPC_SECTORS_PER_FAT) * MPC_SECTOR_SIZE;
export const MPC_FAT_BYTES = MPC_SECTORS_PER_FAT * MPC_SECTOR_SIZE;

// ---- SND file constants ---------------------------------------------------
export const MPC_SND_HEADER_SIZE = 42;
export const MPC_SAMPLE_RATE = 44100;  // MPC2000+ always 44100 Hz
export const MPC_MAX_SAMPLES = 224;    // limited by root directory entries

// ---- FAT12 helpers --------------------------------------------------------

/**
 * Write a FAT12 entry value for a given cluster index.
 */
function setFat12Entry(fat, cluster, value) {
   const offset = cluster + (cluster >> 1); // cluster * 3 / 2
   if (cluster & 1) {
      // Odd cluster: high 12 bits of 3-byte pair
      fat[offset] = (fat[offset] & 0x0F) | ((value & 0x0F) << 4);
      fat[offset + 1] = (value >> 4) & 0xFF;
   } else {
      // Even cluster: low 12 bits of 3-byte pair
      fat[offset] = value & 0xFF;
      fat[offset + 1] = (fat[offset + 1] & 0xF0) | ((value >> 8) & 0x0F);
   }
}

/**
 * Build a FAT12 boot sector (512 bytes) for a 1.44MB floppy.
 */
function buildBootSector() {
   const boot = new Uint8Array(MPC_SECTOR_SIZE);
   const dv = new DataView(boot.buffer);

   // Jump instruction (3 bytes)
   boot[0] = 0xEB; boot[1] = 0x3C; boot[2] = 0x90;

   // OEM name (8 bytes)
   writeAscii(boot, 3, 8, 'MSDOS5.0');

   // BPB (BIOS Parameter Block)
   dv.setUint16(11, MPC_SECTOR_SIZE, true);           // bytes per sector
   boot[13] = MPC_SECTORS_PER_CLUSTER;                 // sectors per cluster
   dv.setUint16(14, MPC_RESERVED_SECTORS, true);       // reserved sectors
   boot[16] = MPC_NUM_FATS;                            // number of FATs
   dv.setUint16(17, MPC_ROOT_ENTRIES, true);           // root entry count
   dv.setUint16(19, MPC_TOTAL_SECTORS, true);          // total sectors (16-bit)
   boot[21] = MPC_MEDIA_DESCRIPTOR;                    // media descriptor
   dv.setUint16(22, MPC_SECTORS_PER_FAT, true);        // sectors per FAT
   dv.setUint16(24, 18, true);                         // sectors per track
   dv.setUint16(26, 2, true);                          // number of heads
   dv.setUint32(28, 0, true);                          // hidden sectors
   dv.setUint32(32, 0, true);                          // total sectors (32-bit, 0 if 16-bit used)

   // Extended boot record (FAT12)
   boot[36] = 0x00;                                    // drive number
   boot[37] = 0x00;                                    // reserved
   boot[38] = 0x29;                                    // boot signature
   dv.setUint32(39, 0x12345678, true);                 // volume serial number
   writeAscii(boot, 43, 11, 'MPC DISK    ');           // volume label (11 chars)
   writeAscii(boot, 54, 8, 'FAT12   ');                // filesystem type (8 chars)

   // Boot code signature at end
   boot[510] = 0x55;
   boot[511] = 0xAA;

   return boot;
}

/**
 * Write ASCII string into buffer at given offset, space-padded.
 */
function writeAscii(buf, off, len, str) {
   const s = (str || '').substring(0, len).padEnd(len, ' ');
   for (let i = 0; i < len; i++) {
      buf[off + i] = s.charCodeAt(i) & 0xFF;
   }
}

/**
 * Sanitize a filename to 8.3 format for FAT12.
 * MPC SND files use .SND extension.
 */
function toFatName(name, ext) {
   // Remove invalid FAT chars, uppercase, truncate
   const clean = (name || 'SAMPLE').toUpperCase().replace(/[^A-Z0-9_]/g, '').substring(0, 8);
   const cleanExt = (ext || 'SND').toUpperCase().replace(/[^A-Z0-9]/g, '').substring(0, 3);
   return { name: clean.padEnd(8, ' '), ext: cleanExt.padEnd(3, ' ') };
}

/**
 * Build a FAT12 directory entry (32 bytes).
 */
function buildDirEntry(name, ext, startCluster, fileSize, isDir) {
   const entry = new Uint8Array(32);
   const dv = new DataView(entry.buffer);

   // Filename (8 bytes) + extension (3 bytes), space-padded
   writeAscii(entry, 0, 8, name);
   writeAscii(entry, 8, 3, ext);

   // Attributes
   entry[11] = isDir ? 0x10 : 0x20; // directory or archive

   // Reserved (10 bytes) — zeros

   // Time and date (FAT format)
   // Time: bits 15-11=hours, 10-5=minutes, 4-0=seconds/2
   // Date: bits 15-9=year+1980, 8-5=month, 4-0=day
   dv.setUint16(16, 0x0000 | (12 << 11) | (0 << 5) | 0, true); // 12:00:00
   dv.setUint16(18, ((2024 - 1980) << 9) | (1 << 5) | 1, true); // 2024-01-01

   // Starting cluster number
   dv.setUint16(20, startCluster & 0xFFFF, true);

   // File size
   dv.setUint32(22, fileSize, true);

   return entry;
}

/**
 * Build an MPC SND file (42-byte header + 16-bit LE PCM).
 *
 * @param {string} name - Sample name (will be truncated to 16 chars)
 * @param {Int16Array} pcm - Mono 16-bit signed PCM data
 * @param {number} sampleRate - Sample rate (default 44100)
 * @returns {Uint8Array} SND file bytes
 */
export function buildSndFile(name, pcm, sampleRate = MPC_SAMPLE_RATE) {
   const headerSize = MPC_SND_HEADER_SIZE;
   const pcmBytes = pcm.length * 2;
   const fileSize = headerSize + pcmBytes;
   const buf = new Uint8Array(fileSize);
   const dv = new DataView(buf.buffer);

   // File type identifier (0x01, 0x04)
   buf[0] = 0x01;
   buf[1] = 0x04;

   // Filename (16 bytes ASCII, space-padded, no extension)
   const sndName = (name || 'SAMPLE').substring(0, 16);
   writeAscii(buf, 2, 16, sndName.padEnd(16, ' '));

   // Null terminator
   buf[0x12] = 0x00;

   // Level (0-200, default 100)
   buf[0x13] = 100;

   // Tune (-120 to +120, signed, default 0)
   buf[0x14] = 0;

   // Channels (0=Mono)
   buf[0x15] = 0;

   // Start point (u32 LE) — always 0
   dv.setUint32(0x16, 0, true);

   // Loop end point (u32 LE) — 0 (no loop)
   dv.setUint32(0x1A, 0, true);

   // End point (u32 LE) = number of samples
   dv.setUint32(0x1E, pcm.length, true);

   // Loop length (u32 LE) — 0
   dv.setUint32(0x22, 0, true);

   // Loop mode (0=Off)
   buf[0x26] = 0;

   // Beats in loop (default 1)
   buf[0x27] = 1;

   // Sample rate (u16 LE)
   dv.setUint16(0x28, sampleRate, true);

   // PCM data: 16-bit signed LE
   for (let i = 0; i < pcm.length; i++) {
      dv.setInt16(headerSize + i * 2, pcm[i], true);
   }

   return buf;
}

/**
 * Calculate the total size of SND files for capacity checking.
 *
 * @param {Array} samples - Array of { pcm: Int16Array }
 * @returns {number} - total bytes including headers
 */
export function calcMpcCapacity(samples) {
   let total = 0;
   for (const smp of samples) {
      if (!smp.pcm || smp.pcm.length === 0) continue;
      total += MPC_SND_HEADER_SIZE + smp.pcm.length * 2;
   }
   return total;
}

/**
 * Check if samples fit on the disk.
 *
 * @param {Array} samples - Array of { pcm: Int16Array }
 * @returns {{ totalBytes: number, fits: boolean, maxBytes: number }}
 */
export function calcMpcLayout(samples) {
   const totalBytes = calcMpcCapacity(samples);
   const maxBytes = MPC_DATA_BYTES;
   return { totalBytes, fits: totalBytes <= maxBytes, maxBytes };
}

/**
 * Build a complete Akai MPC2000 FAT12 floppy disk image with SND files.
 *
 * Each sample becomes a .SND file in the root directory. Files are stored
 * sequentially using FAT12 cluster chains.
 *
 * @param {Array} samples - Array of { name, pcm: Int16Array, sampleRate }
 * @param {object} opts - { volumeLabel: string }
 * @returns {Uint8Array} 1,474,560-byte disk image
 */
export function buildMpcDisk(samples, opts = {}) {
   const volumeLabel = (opts.volumeLabel || 'MPC DISK').substring(0, 11);

   const disk = new Uint8Array(MPC_DISK_SIZE);
   const dv = new DataView(disk.buffer);

   // ---- Boot sector --------------------------------------------------------
   const boot = buildBootSector();
   disk.set(boot, 0);

   // Set volume label in boot sector
   writeAscii(disk, 43, 11, volumeLabel.padEnd(11, ' '));

   // ---- Initialize FATs ----------------------------------------------------
   // FAT entries 0 and 1 are reserved:
   //   entry 0: media descriptor | 0xF00 → 0xFF0 | media
   //   entry 1: end-of-chain marker 0xFFF
   const fat1 = disk.subarray(MPC_FAT1_OFFSET, MPC_FAT1_OFFSET + MPC_FAT_BYTES);
   const fat2 = disk.subarray(MPC_FAT2_OFFSET, MPC_FAT2_OFFSET + MPC_FAT_BYTES);

   // Entry 0: low byte = media descriptor, high nibble = 0xF
   fat1[0] = MPC_MEDIA_DESCRIPTOR;
   fat1[1] = 0xFF;
   fat1[2] = 0xFF;
   // Entry 1: 0xFFF (end of chain)
   // Already set by fat1[1]=0xFF, fat1[2]=0xFF

   // Copy to FAT2
   fat2[0] = MPC_MEDIA_DESCRIPTOR;
   fat2[1] = 0xFF;
   fat2[2] = 0xFF;

   // ---- Build SND files and allocate clusters ------------------------------
   const validSamples = samples.filter(s => s.pcm && s.pcm.length > 0);
   const dirEntries = [];
   let nextCluster = 2; // first data cluster
   let dataOffset = MPC_DATA_START_BYTE;

   for (let i = 0; i < validSamples.length && i < MPC_MAX_SAMPLES; i++) {
      const smp = validSamples[i];
      const sampleRate = smp.sampleRate || MPC_SAMPLE_RATE;
      const sndData = buildSndFile(smp.name, smp.pcm, sampleRate);

      // Calculate clusters needed (1 sector per cluster, 512 bytes)
      const numClusters = Math.ceil(sndData.length / MPC_SECTOR_SIZE);

      // Build FAT chain for this file
      for (let c = 0; c < numClusters; c++) {
         const cluster = nextCluster + c;
         if (c < numClusters - 1) {
            // Point to next cluster
            setFat12Entry(fat1, cluster, cluster + 1);
            setFat12Entry(fat2, cluster, cluster + 1);
         } else {
            // End of chain
            setFat12Entry(fat1, cluster, 0xFFF);
            setFat12Entry(fat2, cluster, 0xFFF);
         }
      }

      // Write file data to disk
      disk.set(sndData, dataOffset);
      dataOffset += numClusters * MPC_SECTOR_SIZE;

      // Create directory entry
      const baseName = (smp.name || `SAMPLE${i + 1}`).replace(/\.SND$/i, '');
      const fat = toFatName(baseName, 'SND');
      const dirEntry = buildDirEntry(fat.name, fat.ext, nextCluster, sndData.length, false);
      dirEntries.push(dirEntry);

      nextCluster += numClusters;
   }

   // ---- Root directory -----------------------------------------------------
   // Volume label entry (first entry)
   const volEntry = new Uint8Array(32);
   writeAscii(volEntry, 0, 11, volumeLabel.padEnd(11, ' '));
   volEntry[11] = 0x08; // volume label attribute
   disk.set(volEntry, MPC_ROOT_DIR_OFFSET);

   // Write directory entries
   for (let i = 0; i < dirEntries.length; i++) {
      disk.set(dirEntries[i], MPC_ROOT_DIR_OFFSET + (i + 1) * 32);
   }

   console.log(`[MPC] buildMpcDisk: ${validSamples.length} SND files, ` +
      `clusters used: ${nextCluster - 2}, disk: ${MPC_DISK_SIZE} bytes`);
   for (let i = 0; i < validSamples.length; i++) {
      const smp = validSamples[i];
      const sndSize = MPC_SND_HEADER_SIZE + smp.pcm.length * 2;
      console.log(`[MPC] file ${i}: name="${smp.name}.SND", ` +
         `pcm=${smp.pcm.length} samples, size=${sndSize} bytes`);
   }

   return disk;
}
